import { useEffect, useState } from 'react';
import { attractAudio } from './attractAudio';
import { getCabinetId } from './easterEggProgress';

export interface AudioPreferences {
  characterVolume: number;
  characterSpeed: number;
  shooterVoiceVolume: number;
  shooterVoiceSpeed: number;
  fightVoiceVolume: number;
  fightVoiceSpeed: number;
  fightMusicVolume: number;
  fightEffectsVolume: number;
  shooterGore: number;
  shooterShake: boolean;
}

export const voiceSpeedSteps = [0.8, 0.9, 1, 1.1, 1.2, 1.25, 1.5, 1.75, 2] as const;
const STORAGE_KEY = 'smackers-audio-preferences-v1';
const defaults: AudioPreferences = {
  characterVolume: 1,
  characterSpeed: 1.2,
  shooterVoiceVolume: 1,
  shooterVoiceSpeed: 1.2,
  fightVoiceVolume: 1,
  fightVoiceSpeed: 1,
  fightMusicVolume: 0.8,
  fightEffectsVolume: 1,
  shooterGore: 1,
  shooterShake: true,
};

const clamp = (value: unknown, minimum: number, maximum: number, fallback: number) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.max(minimum, Math.min(maximum, numeric)) : fallback;
};

const normalize = (value: Partial<AudioPreferences> | null | undefined): AudioPreferences => ({
  characterVolume: clamp(value?.characterVolume, 0, 1.5, defaults.characterVolume),
  characterSpeed: clamp(value?.characterSpeed, 0.75, 2, defaults.characterSpeed),
  shooterVoiceVolume: clamp(value?.shooterVoiceVolume, 0, 1.5, defaults.shooterVoiceVolume),
  shooterVoiceSpeed: clamp(value?.shooterVoiceSpeed, 0.75, 2, defaults.shooterVoiceSpeed),
  fightVoiceVolume: clamp(value?.fightVoiceVolume, 0, 1.5, defaults.fightVoiceVolume),
  fightVoiceSpeed: clamp(value?.fightVoiceSpeed, 0.75, 2, defaults.fightVoiceSpeed),
  fightMusicVolume: clamp(value?.fightMusicVolume, 0, 1, defaults.fightMusicVolume),
  fightEffectsVolume: clamp(value?.fightEffectsVolume, 0, 1.5, defaults.fightEffectsVolume),
  shooterGore: clamp(value?.shooterGore, 0.5, 1.5, defaults.shooterGore),
  shooterShake: typeof value?.shooterShake === 'boolean' ? value.shooterShake : defaults.shooterShake,
});

const readLocal = () => {
  try { return normalize(JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') as Partial<AudioPreferences> | null); } catch { return defaults; }
};

let current = readLocal();
let hydrated = false;
let saveTimer: number | null = null;
const listeners = new Set<(preferences: AudioPreferences) => void>();

const applyAudio = (preferences: AudioPreferences) => {
  attractAudio.setCharacterVoicePreferences(preferences.characterVolume, preferences.characterSpeed);
  attractAudio.setShooterVoicePreferences(preferences.shooterVoiceVolume, preferences.shooterVoiceSpeed);
  attractAudio.setFightVoicePreferences(preferences.fightVoiceVolume, preferences.fightVoiceSpeed);
};

const publish = (preferences: AudioPreferences) => {
  current = normalize(preferences);
  applyAudio(current);
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(current)); } catch { /* Session settings still apply. */ }
  listeners.forEach((listener) => listener(current));
};

const saveRemote = () => {
  if (saveTimer !== null) window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => {
    saveTimer = null;
    void fetch('/api/audio-preferences', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cabinetId: getCabinetId(), ...current }),
      keepalive: true,
    }).catch(() => undefined);
  }, 500);
};

async function hydrate() {
  if (hydrated) return;
  hydrated = true;
  applyAudio(current);
  try {
    const response = await fetch(`/api/audio-preferences?cabinetId=${encodeURIComponent(getCabinetId())}`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const payload = await response.json() as { preferences?: Partial<AudioPreferences> };
    if (payload.preferences) publish(normalize(payload.preferences));
  } catch {
    // Local preferences remain authoritative while offline.
  }
}

export function updateAudioPreferences(change: Partial<AudioPreferences>) {
  publish({ ...current, ...change });
  saveRemote();
}

export function nextVoiceSpeed(speed: number) {
  const index = voiceSpeedSteps.findIndex((step) => step >= speed - 0.001);
  return voiceSpeedSteps[(index + 1 + voiceSpeedSteps.length) % voiceSpeedSteps.length];
}

export function useAudioPreferences() {
  const [preferences, setPreferences] = useState(current);
  useEffect(() => {
    listeners.add(setPreferences);
    void hydrate();
    return () => { listeners.delete(setPreferences); };
  }, []);
  return [preferences, updateAudioPreferences] as const;
}
